import {
  getDedupedCompositeResultsForGroup,
  buildGoodsCompositeSelectionId,
  parseGoodsCompositeSelectionId,
} from '@/utils/goodsCompositeSelection';
import { collectResultIndexesByMainSkc } from './groupGoodsCompositeResultsByMain';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import {
  formatMissingStampError,
  resolveStampImageSourceForIngest,
} from './goodsPrintUrlResolve';
import {
  buildStampRoleMeta,
  buildTitleRoleMeta,
  pickImageSpaceUrlById,
  resolveOriginalLineageImageId,
} from './exportStampRoleMeta';
import {
  IMAGE_ASSET_ROLES,
  collectOriginalUrlsFromImageAssets,
  filterImageAssetsByRole,
  normalizeImageAssetList,
  resolvePrintUrlFromAssetsOrMap,
} from './imageAssetModel';

function isDirectImageUrlString(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  return /^(https?:|blob:|data:image|\/)/i.test(s);
}

function isGoodsCompositeSelectionId(value) {
  return /^composite-\d+-\d+-\d+$/i.test(String(value ?? '').trim());
}

function normalizeImageRef(imageRef) {
  if (!imageRef) return { id: '', url: '' };
  if (typeof imageRef === 'string' || typeof imageRef === 'number') {
    const s = String(imageRef).trim();
    if (isDirectImageUrlString(s)) {
      return { id: '', url: s };
    }
    if (isGoodsCompositeSelectionId(s)) {
      return { id: '', url: '' };
    }
    return { id: s, url: '' };
  }
  if (typeof imageRef === 'object') {
    const idRaw = imageRef.id ?? imageRef.imageId ?? imageRef.value ?? '';
    const id = idRaw ? String(idRaw).trim() : '';
    const url = imageRef.url ?? imageRef.dataUrl ?? imageRef.src ?? imageRef.apiUrl ?? imageRef.previewUrl ?? '';
    if (isGoodsCompositeSelectionId(id)) {
      return { id: '', url: url ? String(url) : '' };
    }
    return { id: id || '', url: url ? String(url) : '' };
  }
  return { id: '', url: '' };
}

function collectOrderedStampNamesFromTemplate(templateData) {
  const seen = new Set();
  const names = [];
  const areas = templateData?.stampAreas || {};
  Object.values(areas).forEach((stamps) => {
    if (!Array.isArray(stamps)) return;
    stamps.forEach((s) => {
      const n = (s && s.name) || '未命名印花';
      if (!seen.has(n)) {
        seen.add(n);
        names.push(n);
      }
    });
  });
  return names;
}

function stampAssignmentIsEmpty(v) {
  if (v == null || v === '') return true;
  if (typeof v === 'object') {
    const id = String(v.id ?? v.imageId ?? v.value ?? '').trim();
    const url = String(v.url ?? v.dataUrl ?? v.src ?? '').trim();
    return !id && !url;
  }
  return !String(v).trim();
}

function augmentImageGroupFromTaskImageIds(form, groupIndex, rawImageGroup, taskImageIds) {
  const group =
    rawImageGroup && typeof rawImageGroup === 'object' && !Array.isArray(rawImageGroup)
      ? { ...rawImageGroup }
      : {};
  const stampNames = collectOrderedStampNamesFromTemplate(form.templateData);
  if (stampNames.length === 0) return group;

  const ids = (Array.isArray(taskImageIds) ? taskImageIds : [])
    .map(String)
    .map((id) => id.trim())
    .filter((id) => id && !isGoodsCompositeSelectionId(id));
  if (ids.length === 0) return group;

  const needsFill = stampNames.some((name) => stampAssignmentIsEmpty(group[name]));
  if (!needsFill) return group;

  const stampsPerGroup = stampNames.length;
  const start = groupIndex * stampsPerGroup;
  stampNames.forEach((name, i) => {
    if (!stampAssignmentIsEmpty(group[name])) return;
    const id = ids[start + i];
    if (id) group[name] = id;
  });
  return group;
}

function collectStampEntriesForListing(form, groupIndex, taskImageIds) {
  const rawImageGroup = form.imageGroups?.[groupIndex] || {};
  const imageGroup = augmentImageGroupFromTaskImageIds(form, groupIndex, rawImageGroup, taskImageIds);
  const stampEntries = Object.entries(imageGroup);
  const ids = (Array.isArray(taskImageIds) ? taskImageIds : []).map(String).filter(Boolean);
  const taskFallbackStampId = ids.find(Boolean) || '';
  const safeEntries =
    stampEntries.length > 0 ? stampEntries : taskFallbackStampId ? [['默认印花', taskFallbackStampId]] : [];
  return { safeEntries, taskFallbackStampId };
}

/**
 * 导出预览列归属：
 * - kind=main → 主图
 * - kind=detail → 详情图
 * - POD 商品设计 → 详情图
 * - 主图创作 → 主图
 * 合并同源行时若无主图，再用 POD 详情图第一张顶上（见 mergeExportPreviewRowsByLinkKey）
 */
export function resolveExportPreviewImageColumn(result, form, billingNav = 'goods') {
  const kind = String(result?.kind || '').trim().toLowerCase();
  if (kind === 'main') return 'main';
  if (kind === 'detail') return 'detail';
  if (billingNav === 'podProductDesign') return 'detail';
  return 'main';
}

/** 构建按模版导出的预览行（与详情页印花/成品展示逻辑一致） */
export async function buildGoodsExportPreviewData({
  goodsForms,
  taskImageIds,
  imageSpaceImages,
  cropImageUrlMap,
  selectedImages,
  printUrlByImageId = {},
  imageAssets = null,
  titleByCompositeId = null,
  fallbackStampUrls = [],
  billingNav = 'goods',
}) {
  const previewData = [];
  const selectedSet =
    selectedImages instanceof Set ? selectedImages : new Set(selectedImages || []);
  const ids = (Array.isArray(taskImageIds) ? taskImageIds : []).map(String).filter(Boolean);
  const taskFallbackStampId = ids.find(Boolean) || '';
  const titleMap = titleByCompositeId instanceof Map ? titleByCompositeId : new Map();
  const stampFallbackUrls = (Array.isArray(fallbackStampUrls) ? fallbackStampUrls : [])
    .map((url) => String(url || '').trim())
    .filter(Boolean);
  const resolvedBillingNav = billingNav === 'podProductDesign' ? 'podProductDesign' : 'goods';
  const normalizedAssets = normalizeImageAssetList(imageAssets);
  // 方案三：导出印花只读 print 资产 URL，并入旧映射（资产优先）
  const printAssets = filterImageAssetsByRole(normalizedAssets, IMAGE_ASSET_ROLES.PRINT);
  const effectivePrintUrlByImageId = {
    ...(printUrlByImageId && typeof printUrlByImageId === 'object' ? printUrlByImageId : {}),
  };
  printAssets.forEach((asset) => {
    if (asset.legacyImageId) effectivePrintUrlByImageId[asset.legacyImageId] = asset.url;
    effectivePrintUrlByImageId[asset.assetId] = asset.url;
  });
  const assetOriginalUrls = collectOriginalUrlsFromImageAssets(normalizedAssets);

  const resolveCompositeImageUrl = (result) => {
    const url = result?.compositeUrl || result?.url || result?.dataUrl || '';
    return String(url || '').trim();
  };

  const resolveOrderedStampEntries = (form, groupIndex, pickedResults = [], dedupedResults = []) => {
    const { safeEntries } = collectStampEntriesForListing(form, groupIndex, taskImageIds);
    const entryMap = new Map(safeEntries);
    [...pickedResults, ...dedupedResults].forEach((result) => {
      const groups = result?.replacedImageGroups;
      if (!groups || typeof groups !== 'object') return;
      Object.entries(groups).forEach(([name, ref]) => {
        if (!name || ref == null || ref === '') return;
        if (!entryMap.has(name)) entryMap.set(name, ref);
      });
    });
    if (entryMap.size === 0 && String(form?.templateId || '') === 'goods-studio-ai') {
      const url = stampFallbackUrls[groupIndex] || stampFallbackUrls[0];
      if (url) {
        const stampId = ids
          .map(String)
          .map((id) => id.trim())
          .filter((id) => id && !isGoodsCompositeSelectionId(id))[groupIndex]
          || ids
            .map(String)
            .map((id) => id.trim())
            .filter((id) => id && !isGoodsCompositeSelectionId(id))[0]
          || '';
        entryMap.set(
          `印花${groupIndex + 1}`,
          stampId
            ? { id: stampId, url, name: `印花${groupIndex + 1}` }
            : url,
        );
      }
    }
    if (entryMap.size === 0) return [];
    const orderedNames = collectOrderedStampNamesFromTemplate(form.templateData);
    const ordered = [];
    const seen = new Set();
    orderedNames.forEach((name) => {
      if (!entryMap.has(name) || seen.has(name)) return;
      ordered.push([name, entryMap.get(name)]);
      seen.add(name);
    });
    entryMap.forEach((imageId, name) => {
      if (seen.has(name)) return;
      ordered.push([name, imageId]);
      seen.add(name);
    });
    return ordered;
  };

  const resolveStampImages = async (form, groupIndex, pickedResults, dedupedResults) => {
    const orderedEntries = resolveOrderedStampEntries(form, groupIndex, pickedResults, dedupedResults);
    const stampImages = [];
    const stampNames = [];
    const missingStampNames = [];
    const originalImages = [];
    const seenOriginalKeys = new Set();

    for (const [stampName, imageId] of orderedEntries) {
      const primaryResult = pickedResults[0];
      const fallbackRefFromResult =
        primaryResult?.replacedImageGroups?.[stampName]
        || dedupedResults.find((r) => r?.replacedImageGroups?.[stampName])?.replacedImageGroups?.[stampName]
        || null;
      const effectiveImageRef = fallbackRefFromResult || imageId || taskFallbackStampId;
      const norm = normalizeImageRef(effectiveImageRef);
      const resolvedId = norm.id || String(imageId || '');
      const stampImage = imageSpaceImages.find((img) => String(img.id) === String(resolvedId));
      const displayName = stampImage?.name
        ? stampImage.name.replace(/\.[^/.]+$/, '')
        : stampName;
      stampNames.push(displayName);

      const stampImageUrl = await resolveStampImageSourceForIngest(
        {
          ...norm,
          // 资产模型优先：role=print 的 URL 写入内联，避免回退原图
          url: resolvePrintUrlFromAssetsOrMap(resolvedId, {
            imageAssets: normalizedAssets,
            printUrlByImageId: effectivePrintUrlByImageId,
          }) || norm.url,
        },
        cropImageUrlMap,
        imageSpaceImages,
        effectivePrintUrlByImageId,
      );
      if (!stampImageUrl) {
        missingStampNames.push(displayName || stampName);
        continue;
      }

      const originalImageId = resolveOriginalLineageImageId(resolvedId);
      let originalUrl = '';
      if (originalImageId && originalImageId !== resolvedId) {
        originalUrl = pickImageSpaceUrlById(originalImageId, imageSpaceImages);
        if (!originalUrl) {
          try {
            const fromDb = await getImageFromIndexedDB(originalImageId);
            if (fromDb && String(fromDb).trim()) originalUrl = String(fromDb).trim();
          } catch (_) {
            /* ignore */
          }
        }
      } else if (
        originalImageId
        && cropImageUrlMap?.[originalImageId]
        && String(cropImageUrlMap[originalImageId]).trim() === stampImageUrl
      ) {
        // 印花来自裁剪表：原图 id 即 map key，尝试取图片空间原图
        originalUrl = pickImageSpaceUrlById(originalImageId, imageSpaceImages);
      }

      const roleMeta = buildStampRoleMeta({
        name: displayName,
        imageId: resolvedId,
        printUrl: stampImageUrl,
        originalImageId,
        originalUrl,
        cropImageUrlMap,
      });
      stampImages.push(roleMeta);

      if (roleMeta.originalUrl) {
        const oKey = roleMeta.originalImageId || roleMeta.originalUrl;
        if (!seenOriginalKeys.has(oKey)) {
          seenOriginalKeys.add(oKey);
          originalImages.push({
            name: `${displayName}·原图`,
            imageId: roleMeta.originalImageId,
            url: roleMeta.originalUrl,
            sourceKind: 'original',
            sourceLabel: '原图',
          });
        }
      }
    }

    if (orderedEntries.length > 0 && missingStampNames.length > 0) {
      throw new Error(formatMissingStampError(missingStampNames));
    }

    // 方案三：资产库中的 original 角色并入原图列（默认不进印花槽）
    assetOriginalUrls.forEach((url, index) => {
      const oKey = `asset-original-${url}`;
      if (seenOriginalKeys.has(oKey)) return;
      seenOriginalKeys.add(oKey);
      originalImages.push({
        name: `原图${index + 1}`,
        imageId: '',
        url,
        sourceKind: 'original',
        sourceLabel: '原图',
        role: IMAGE_ASSET_ROLES.ORIGINAL,
      });
    });

    return {
      stampNames,
      combinedStampName: stampNames.join('+'),
      stampImages,
      originalImages,
      stampImageUrl: stampImages[0]?.url || null,
      stampImageId: stampImages[0]?.imageId || '',
    };
  };

  const appendRow = async (formIndex, groupIndex, resultIndexes) => {
    const form = goodsForms[formIndex];
    if (!form) return;

    const dedupedResults = getDedupedCompositeResultsForGroup(form, groupIndex);
    const pickedResults = resultIndexes
      .map((ri) => dedupedResults[ri])
      .filter(Boolean);
    if (pickedResults.length === 0) return;

    const orderedEntries = resolveOrderedStampEntries(form, groupIndex, pickedResults, dedupedResults);
    if (orderedEntries.length === 0) return;

    const stampPack = await resolveStampImages(form, groupIndex, pickedResults, dedupedResults);
    const mainImages = [];
    const detailImages = [];
    pickedResults.forEach((result) => {
      const url = resolveCompositeImageUrl(result);
      if (!url) return;
      const kind = String(result?.kind || '').trim();
      const item = {
        url,
        imageId: result.imageId,
        kind,
      };
      const column = resolveExportPreviewImageColumn(result, form, resolvedBillingNav);
      if (column === 'main') {
        mainImages.push(item);
      } else {
        detailImages.push(item);
      }
    });
    const compositeImages = [...mainImages, ...detailImages];
    if (compositeImages.length === 0) return;

    const linkedTitles = resultIndexes
      .map((ri) => titleMap.get(buildGoodsCompositeSelectionId(formIndex, groupIndex, ri)))
      .map((meta) => {
        if (!meta) return null;
        if (typeof meta === 'string') return { title: meta.trim(), language: '' };
        const title = String(meta.title || '').trim();
        if (!title) return null;
        return { title, language: String(meta.language || '').trim() };
      })
      .filter(Boolean);
    const aiTitle = linkedTitles[0]?.title || '';
    const titleLanguage = linkedTitles[0]?.language || '';
    const compositeIds = resultIndexes.map((ri) => buildGoodsCompositeSelectionId(formIndex, groupIndex, ri));
    const titleRole = buildTitleRoleMeta({
      title: aiTitle,
      language: titleLanguage,
      sourceKind: aiTitle ? 'ai_copywriting' : 'none',
      compositeIds,
    });

    const resultKey = resultIndexes.join('-');
    const resultImages = [...mainImages, ...detailImages];
    previewData.push({
      id: `export-${formIndex}-${groupIndex}-${resultKey}`,
      formIndex,
      groupIndex,
      resultIndexes,
      stampName: stampPack.combinedStampName,
      displayName: titleRole.displayName,
      layerStampName: stampPack.combinedStampName,
      stampImageId: stampPack.stampImageId,
      stampImageUrl: stampPack.stampImageUrl,
      stampImages: stampPack.stampImages,
      originalImages: stampPack.originalImages || [],
      mainImages,
      detailImages,
      /** 方案二：成果图槽 = 主图 + 细节图 */
      resultImages,
      compositeImages,
      title: aiTitle || null,
      titleLanguage: titleLanguage || null,
      titleRole,
      compositeIds,
    });
  };

  const appendRowsForPrintGroup = async (formIndex, groupIndex, selectedResultIndexes = null) => {
    const form = goodsForms[formIndex];
    if (!form) return;
    const skcIndexLists = collectResultIndexesByMainSkc(form, groupIndex, {
      billingNav: resolvedBillingNav,
      selectedResultIndexes,
    });
    if (skcIndexLists.length === 0) return;
    for (const resultIndexes of skcIndexLists) {
      await appendRow(formIndex, groupIndex, resultIndexes);
    }
  };

  if (selectedSet.size > 0) {
    const grouped = new Map();
    for (const selId of selectedSet) {
      const info = parseGoodsCompositeSelectionId(selId);
      if (!info) continue;
      const key = `${info.formIndex}-${info.groupIndex}`;
      let bucket = grouped.get(key);
      if (!bucket) {
        bucket = {
          formIndex: info.formIndex,
          groupIndex: info.groupIndex,
          resultIndexes: [],
        };
        grouped.set(key, bucket);
      }
      bucket.resultIndexes.push(info.resultIndex);
    }
    for (const bucket of grouped.values()) {
      const resultIndexes = [...new Set(bucket.resultIndexes)].sort((a, b) => a - b);
      await appendRowsForPrintGroup(bucket.formIndex, bucket.groupIndex, resultIndexes);
    }
  } else {
    for (let formIndex = 0; formIndex < goodsForms.length; formIndex++) {
      const form = goodsForms[formIndex];
      const groupCount = form.imageGroups?.length || 0;
      for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
        const deduped = getDedupedCompositeResultsForGroup(form, groupIndex);
        if (deduped.length === 0) continue;
        await appendRowsForPrintGroup(formIndex, groupIndex);
      }
    }
  }

  return previewData;
}

async function preferExportPublicUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const { toExportPublicUrl } = await import('@/utils/exportPublicImageUrl');
    const publicUrl = await toExportPublicUrl(raw);
    return publicUrl || raw;
  } catch {
    return raw;
  }
}

async function enrichImageListForExport(images) {
  return Promise.all(
    (Array.isArray(images) ? images : []).map(async (img) => {
      if (!img || typeof img !== 'object') return img;
      let url = img.url;
      if (!url && img.imageId) {
        try {
          url = await getImageFromIndexedDB(img.imageId);
        } catch (error) {
          console.warn(`无法加载结果图 ${img.imageId}:`, error);
        }
      }
      const resolved = String(url || img.url || img.previewUrl || '').trim();
      const publicUrl = resolved ? await preferExportPublicUrl(resolved) : '';
      return {
        ...img,
        url: publicUrl || resolved || img.url,
        previewUrl: publicUrl || resolved || img.previewUrl || '',
      };
    }),
  );
}

/** 导出前补全印花 / 成品图 URL（IndexedDB），并尽量预换公网链 */
export async function enrichExportPreviewRowsForDownload(rows, {
  cropImageUrlMap = {},
  imageSpaceImages = [],
  printUrlByImageId = {},
} = {}) {
  return Promise.all(
    (Array.isArray(rows) ? rows : []).map(async (item) => {
      // 用户在预览里切换后的 stampImages 优先
      const activeStampUrl = Array.isArray(item.stampImages)
        ? String(item.stampImages.find((s) => s?.url)?.url || '').trim()
        : '';
      let stampImageUrl = activeStampUrl || item.stampImageUrl;
      if (!stampImageUrl && item.stampImageId) {
        stampImageUrl = await resolveStampImageSourceForIngest(
          normalizeImageRef(item.stampImageId),
          cropImageUrlMap,
          imageSpaceImages,
          printUrlByImageId,
        );
      }
      if (stampImageUrl) {
        stampImageUrl = await preferExportPublicUrl(stampImageUrl);
      }

      const [compositeImages, detailImages, mainImages, stampImages, originalImages] = await Promise.all([
        enrichImageListForExport(item.compositeImages),
        enrichImageListForExport(item.detailImages),
        enrichImageListForExport(item.mainImages),
        enrichImageListForExport(item.stampImages),
        enrichImageListForExport(item.originalImages),
      ]);
      const resultImages = [...mainImages, ...detailImages];

      return {
        ...item,
        stampImageUrl,
        stampImages,
        originalImages,
        compositeImages,
        detailImages,
        mainImages,
        resultImages,
      };
    }),
  );
}
